import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Send, Calendar } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CampaignCreate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [campaign, setCampaign] = useState({
    name: '',
    subject: '',
    from_email: '',
    from_name: '',
    template_id: '',
    domain_id: '',
    subscriber_ids: [],
    scheduled_at: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, domainsRes] = await Promise.all([
        axios.get(`${API}/templates`, { headers: getAuthHeaders() }),
        axios.get(`${API}/domains`, { headers: getAuthHeaders() })
      ]);
      setTemplates(templatesRes.data);
      setDomains(domainsRes.data);
      
      if (domainsRes.data.length > 0) {
        setCampaign(prev => ({ ...prev, domain_id: domainsRes.data[0].id }));
      }
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const loadSubscribers = async (domainId) => {
    try {
      const response = await axios.get(`${API}/subscribers?domain_id=${domainId}`, { headers: getAuthHeaders() });
      setSubscribers(response.data.filter(s => s.subscribed));
    } catch (error) {
      toast.error('Failed to load subscribers');
    }
  };

  useEffect(() => {
    if (campaign.domain_id) {
      loadSubscribers(campaign.domain_id);
    }
  }, [campaign.domain_id]);

  const handleSubscriberToggle = (subscriberId) => {
    setCampaign(prev => ({
      ...prev,
      subscriber_ids: prev.subscriber_ids.includes(subscriberId)
        ? prev.subscriber_ids.filter(id => id !== subscriberId)
        : [...prev.subscriber_ids, subscriberId]
    }));
  };

  const selectAllSubscribers = () => {
    setCampaign(prev => ({
      ...prev,
      subscriber_ids: subscribers.map(s => s.id)
    }));
  };

  const deselectAll = () => {
    setCampaign(prev => ({ ...prev, subscriber_ids: [] }));
  };

  const handleSubmit = async (scheduleNow = true) => {
    if (!campaign.name || !campaign.subject || !campaign.from_email || !campaign.template_id) {
      toast.error('Please fill all required fields');
      return;
    }

    if (campaign.subscriber_ids.length === 0) {
      toast.error('Please select at least one subscriber');
      return;
    }

    if (!scheduleNow && !campaign.scheduled_at) {
      toast.error('Please select a schedule date/time');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...campaign,
        scheduled_at: scheduleNow ? null : campaign.scheduled_at
      };

      await axios.post(`${API}/campaigns`, payload, { headers: getAuthHeaders() });
      toast.success(scheduleNow ? 'Campaign sent!' : 'Campaign scheduled!');
      navigate('/campaigns');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              onClick={() => navigate('/campaigns')}
              variant="outline"
              className="mb-4 border-[#2A2D36] text-[#9CA3AF]"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Campaigns
            </Button>
            <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
              Create Campaign
            </h1>
            <p className="text-lg text-[#9CA3AF]">
              Step {step} of 3
            </p>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full ${
                  s <= step ? 'bg-[#183871]' : 'bg-[#2A2D36]'
                }`}
              />
            ))}
          </div>

          {/* Step 1: Campaign Details */}
          {step === 1 && (
            <div className="space-y-6 rounded-lg border border-[#2A2D36] bg-[#13151A]/80 p-6">
              <h2 className="text-2xl font-semibold text-[#F3F4F6]">Campaign Details</h2>
              
              <div className="space-y-2">
                <Label className="text-[#F3F4F6]">Campaign Name *</Label>
                <Input
                  value={campaign.name}
                  onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                  placeholder="e.g., January Newsletter"
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#F3F4F6]">Email Subject *</Label>
                <Input
                  value={campaign.subject}
                  onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })}
                  placeholder="e.g., Our Latest Updates"
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#F3F4F6]">From Email *</Label>
                  <Input
                    type="email"
                    value={campaign.from_email}
                    onChange={(e) => setCampaign({ ...campaign, from_email: e.target.value })}
                    placeholder="info@dommo.ch"
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#F3F4F6]">From Name *</Label>
                  <Input
                    value={campaign.from_name}
                    onChange={(e) => setCampaign({ ...campaign, from_name: e.target.value })}
                    placeholder="Dommo Team"
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#F3F4F6]">Domain *</Label>
                <select
                  value={campaign.domain_id}
                  onChange={(e) => setCampaign({ ...campaign, domain_id: e.target.value })}
                  className="w-full px-4 py-2 rounded-md bg-[#1F2128] border border-[#2A2D36] text-[#F3F4F6]"
                >
                  {domains.map((domain) => (
                    <option key={domain.id} value={domain.id}>{domain.domain}</option>
                  ))}
                </select>
              </div>

              <Button
                onClick={() => setStep(2)}
                className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white"
                disabled={!campaign.name || !campaign.subject || !campaign.from_email || !campaign.from_name}
              >
                Next: Select Template
              </Button>
            </div>
          )}

          {/* Step 2: Select Template */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 p-6">
                <h2 className="text-2xl font-semibold text-[#F3F4F6] mb-4">Select Template</h2>
                
                {templates.length === 0 ? (
                  <p className="text-[#9CA3AF] text-center py-8">No templates available</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => setCampaign({ ...campaign, template_id: template.id })}
                        className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${
                          campaign.template_id === template.id
                            ? 'border-[#183871] bg-[#183871]/10'
                            : 'border-[#2A2D36] hover:border-[#183871]/50'
                        }`}
                      >
                        <h3 className="text-[#F3F4F6] font-semibold mb-2">{template.name}</h3>
                        <div className="h-32 bg-[#1F2128] rounded overflow-hidden">
                          <iframe
                            srcDoc={template.html}
                            className="w-full h-full pointer-events-none transform scale-50 origin-top-left"
                            style={{ width: '200%', height: '200%' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 border-[#2A2D36] text-[#9CA3AF]"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  className="flex-1 bg-[#183871] hover:bg-[#1F4BA1] text-white"
                  disabled={!campaign.template_id}
                >
                  Next: Select Recipients
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Select Recipients & Send */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-[#F3F4F6]">Select Recipients</h2>
                  <div className="flex gap-2">
                    <Button
                      onClick={selectAllSubscribers}
                      size="sm"
                      variant="outline"
                      className="border-[#2A2D36] text-[#9CA3AF]"
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={deselectAll}
                      size="sm"
                      variant="outline"
                      className="border-[#2A2D36] text-[#9CA3AF]"
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>

                {subscribers.length === 0 ? (
                  <p className="text-[#9CA3AF] text-center py-8">No subscribers available for this domain</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {subscribers.map((subscriber) => (
                      <label
                        key={subscriber.id}
                        className="flex items-center gap-3 p-3 rounded-md bg-[#1F2128] border border-[#2A2D36] hover:border-[#183871]/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={campaign.subscriber_ids.includes(subscriber.id)}
                          onChange={() => handleSubscriberToggle(subscriber.id)}
                          className="w-4 h-4 text-[#183871] bg-[#1F2128] border-[#2A2D36] rounded"
                        />
                        <div className="flex-1">
                          <p className="text-[#F3F4F6] font-medium">{subscriber.email}</p>
                          {subscriber.name && (
                            <p className="text-sm text-[#9CA3AF]">{subscriber.name}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                <div className="mt-4 p-3 rounded-md bg-[#183871]/10 border border-[#183871]/20">
                  <p className="text-sm text-[#F3F4F6]">
                    Selected: <strong>{campaign.subscriber_ids.length}</strong> recipients
                  </p>
                </div>
              </div>

              {/* Schedule Options */}
              <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 p-6">
                <h2 className="text-2xl font-semibold text-[#F3F4F6] mb-4">Schedule</h2>
                
                <div className="space-y-2">
                  <Label className="text-[#F3F4F6]">Schedule Date/Time (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={campaign.scheduled_at}
                    onChange={(e) => setCampaign({ ...campaign, scheduled_at: e.target.value })}
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                  />
                  <p className="text-xs text-[#9CA3AF]">
                    Leave empty to send immediately
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="flex-1 border-[#2A2D36] text-[#9CA3AF]"
                >
                  Back
                </Button>
                {campaign.scheduled_at ? (
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={loading || campaign.subscriber_ids.length === 0}
                    className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white"
                  >
                    <Calendar size={16} className="mr-2" />
                    {loading ? 'Scheduling...' : 'Schedule Campaign'}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSubmit(true)}
                    disabled={loading || campaign.subscriber_ids.length === 0}
                    className="flex-1 bg-[#183871] hover:bg-[#1F4BA1] text-white"
                  >
                    <Send size={16} className="mr-2" />
                    {loading ? 'Sending...' : 'Send Now'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CampaignCreate;
